/**
 * 018: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/financial/dashboard/transaction-history-table/columns.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Badge, Text } from "rizzui";
import { PiArrowClockwiseDuotone as RetryIcon, PiEyeDuotone as EyeIcon, PiReceiptDuotone as OrderIcon } from "react-icons/pi";
import { getStatusBadge } from "@steelyard/theme";
import { canRetry, count, FAILED, moment, money, QUEUED, SUCCEEDED, type OutstandingRefund } from "../../lib/services/finance";
import { sentenceCase } from "../../lib/format";
import { RowActions } from "../../components/table/row-actions";
import { orderPath, refundPath } from "../../lib/paths";

const columnHelper = createColumnHelper<OutstandingRefund>();

/**
 * No selection column — no bulk action exists on this list. Status routes through `getStatusBadge`'s
 * own `queued`/`failed` entries, so the tone lives in one map; the plain-words meaning of each sits
 * under it. `mayRetry` only decides whether the row menu offers "Retry…", which opens the detail
 * page's own `?confirm=retry` dialog — the retry itself is never sent from the list.
 */
/** Order number and customer are Billing's own labels; neither ever falls back to an id. */
export function refundColumns(mayRetry: boolean) {
  return [
    // The API's decimal parsed to a number so the sort compares amounts; nothing computed.
    columnHelper.accessor((refund) => (refund.amount === null || refund.amount === undefined ? undefined : Number(refund.amount)), {
      id: "amount",
      header: "Refund",
      size: 180,
      sortUndefined: "last",
      cell: ({ row }) => (
        <Link to={refundPath(row.original.refundNumber || row.original.refundId)} className="flex flex-col hover:underline">
          <span className="font-lexend font-semibold tabular-nums text-gray-900">{money(row.original.amount)}</span>
          <span className="text-xs text-gray-500">{row.original.customerName ?? "Unknown customer"}</span>
        </Link>
      ),
    }),
    columnHelper.accessor("status", {
      header: "State",
      size: 190,
      cell: ({ row }) => {
        const { status } = row.original;
        const meaning =
          status === QUEUED ? "Not reached the provider"
          : status === FAILED ? "Provider refused"
          : status === SUCCEEDED ? "Refunded by the provider"
          : "Not a state this screen knows";
        return (
          <span className="flex flex-col items-start gap-1">
            {getStatusBadge(status)}
            <Text as="span" className="text-xs text-gray-500">
              {meaning}
            </Text>
          </span>
        );
      },
    }),
    columnHelper.accessor("reason", {
      header: "Reason",
      size: 180,
      cell: ({ row }) => (
        <Badge variant="flat" color="secondary" rounded="pill">
          {sentenceCase(row.original.reason)}
        </Badge>
      ),
    }),
    columnHelper.accessor((refund) => refund.orderNumber ?? "", {
      id: "orderId",
      header: "Order",
      size: 170,
      cell: ({ row }) => (
        <span className="flex flex-col items-start gap-1">
          <Link to={orderPath(row.original.orderNumber || row.original.orderId)} className="font-mono text-sm hover:underline">
            {row.original.orderNumber ?? "—"}
          </Link>
          <Badge variant="outline" size="sm" rounded="md">
            {row.original.provider}
          </Badge>
        </span>
      ),
    }),
    columnHelper.accessor((refund) => Number(refund.attempts), {
      id: "attempts",
      header: "Attempts",
      size: 110,
      meta: { align: "right" },
      cell: ({ row }) => (
        <Badge variant="flat" color="info" rounded="pill" className="tabular-nums">
          {count(row.original.attempts)}
        </Badge>
      ),
    }),
    columnHelper.accessor("lastAttemptAt", {
      header: "Last attempt",
      size: 200,
      cell: ({ row }) => <span className="text-sm text-gray-500">{moment(row.original.lastAttemptAt)}</span>,
    }),
    // No delete: a refund is a financial record, and no endpoint removes one.
    columnHelper.display({
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }) => (
        <RowActions
          name={`refund for ${row.original.orderNumber ?? "an order"}`}
          primary={{ label: "View refund", icon: EyeIcon, to: refundPath(row.original.refundNumber || row.original.refundId) }}
          more={[
            mayRetry && canRetry(row.original) && { label: "Retry…", icon: RetryIcon, to: `${refundPath(row.original.refundNumber || row.original.refundId)}?confirm=retry` },
            { label: "Open order", icon: OrderIcon, to: orderPath(row.original.orderNumber || row.original.orderId) },
          ]}
        />
      ),
    }),
  ];
}
